import React, { useEffect, useRef, useState, useCallback } from "react";
import { ResponsivePie } from "@nivo/pie";
import * as htmlToImage from "html-to-image";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
  Stack,
} from "@mui/material";
import { Download, RefreshCw } from "lucide-react";
import genderIcon from "../../assets/gender.png";

/* ------------------------------- Types ------------------------------- */
interface Test {
  gender: "Male" | "Female" | null | undefined;
}

interface PieData {
  id: string;
  value: number;
  color: string;
}

interface PieChartProps {
  height?: number;
}

/* ----------------------------- Component ----------------------------- */
const PieChart: React.FC<PieChartProps> = ({ height = 160 }) => {
  const chartRef = useRef<HTMLDivElement>(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const [tests, setTests] = useState<Test[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  /* ---------- Colors ---------- */
  const maleColor = "#4B5563";
  const femaleColor = "#A8C5DA";

  /* ---------- Fetch Data ---------- */
  const fetchTests = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/tests`);
      const data = await res.json();
      setTests(data.filter((t: Test) => t.gender));
    } catch (e) {
      console.error(e);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  /* ---------- Compute ---------- */
  const male = tests.filter((t) => t.gender === "Male").length;
  const female = tests.filter((t) => t.gender === "Female").length;
  const total = male + female;

  const pieData: PieData[] = [
    { id: "Male", value: male, color: maleColor },
    { id: "Female", value: female, color: femaleColor },
  ].filter((d) => d.value > 0);

  /* ---------- Export ---------- */
  const exportPNG = async () => {
    if (!chartRef.current) return;
    const dataUrl = await htmlToImage.toPng(chartRef.current, {
      backgroundColor: "#273142",
    });
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = "gender_distribution.png";
    link.click();
  };

  /* ---------- Loading ---------- */
  if (loading) {
    return (
      <Box
        sx={{
          height: height + 80,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#273142",
          borderRadius: 2,
        }}
      >
        <CircularProgress size={22} />
      </Box>
    );
  }

  /* ----------------------------- Render ----------------------------- */
  return (
    <Box sx={{ bgcolor: "#273142", borderRadius: 2, p: 2 }}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <img src={genderIcon} alt="Gender" style={{ width: 18, height: 18 }} />
          <Typography fontSize={14} fontWeight={500} color="white">
            Gender
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchTests} disabled={refreshing}>
              <RefreshCw size={14} color="white" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export">
            <IconButton size="small" onClick={exportPNG}>
              <Download size={14} color="white" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Chart + Stats */}
      <Box
        ref={chartRef}
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          gap: 2,
        }}
      >
        {/* Male */}
        <Box textAlign="center">
          <Typography fontSize={22} fontWeight={500} color="#FFFFFF">
            {male}
          </Typography>
          <Typography fontSize={12} color="#FFFFFF">
            Male ({total ? ((male / total) * 100).toFixed(1) : 0}%)
          </Typography>
        </Box>

        {/* Pie */}
        <Box sx={{ width: height, height }}>
          <ResponsivePie
            data={pieData}
            innerRadius={0.65}
            padAngle={1.5}
            cornerRadius={4}
            colors={{ datum: "data.color" }}

            /* ✅ FULLY disable hover & animation */
            animate={false}
            isInteractive={false}
            tooltip={() => null}
            layers={["arcs"]}

            enableArcLabels={false}
            enableArcLinkLabels={false}
            borderWidth={0}
          />
        </Box>

        {/* Female */}
        <Box textAlign="center">
          <Typography fontSize={22} fontWeight={500} color={femaleColor}>
            {female}
          </Typography>
          <Typography fontSize={12} color={femaleColor}>
            Female ({total ? ((female / total) * 100).toFixed(1) : 0}%)
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PieChart;
